import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

type RateLimitFormKey =
	| "windowAllowance"
	| "windowDurationS"
	| "globalHttpRateLimitMax"
	| "globalHttpRateLimitDurationS"
	| "adminLoginRateLimitMax"
	| "adminLoginRateLimitDurationS"
	| "createRoomRateLimitMax"
	| "createRoomRateLimitDurationS"
	| "messageMaxContentLength";

type RateLimitField = {
	key: RateLimitFormKey;
	label: string;
	placeholder: string;
};

const RATE_LIMIT_FIELDS: RateLimitField[] = [
	{ key: "globalHttpRateLimitMax", label: "Global HTTP Allowance", placeholder: "e.g. 40" },
	{ key: "globalHttpRateLimitDurationS", label: "Global HTTP Window (s)", placeholder: "e.g. 60" },
	{ key: "adminLoginRateLimitMax", label: "Admin Login Allowance", placeholder: "e.g. 20" },
	{ key: "adminLoginRateLimitDurationS", label: "Admin Login Window (s)", placeholder: "e.g. 900" },
	{ key: "createRoomRateLimitMax", label: "Create Room Allowance", placeholder: "e.g. 5" },
	{ key: "createRoomRateLimitDurationS", label: "Create Room Window (s)", placeholder: "e.g. 600" },
	{ key: "windowAllowance", label: "WebSocket Message Allowance", placeholder: "e.g. 8" },
	{ key: "windowDurationS", label: "WebSocket Message Window (s)", placeholder: "e.g. 10" },
	{ key: "messageMaxContentLength", label: "Message Max Length", placeholder: "e.g. 200" },
];

const DEFAULT_RATE_LIMIT_FORM: Record<RateLimitFormKey, string> = {
	windowAllowance: "4",
	windowDurationS: "10",
	globalHttpRateLimitMax: "40",
	globalHttpRateLimitDurationS: "60",
	adminLoginRateLimitMax: "20",
	adminLoginRateLimitDurationS: "900",
	createRoomRateLimitMax: "5",
	createRoomRateLimitDurationS: "600",
	messageMaxContentLength: "200",
};

type RateLimitConfigResponse = {
	rlWindowAllowance: number;
	rlWindowDurationMs: number;
	globalHttpRateLimitMax: number;
	globalHttpRateLimitDurationMs: number;
	adminLoginRateLimitMax: number;
	adminLoginRateLimitDurationMs: number;
	createRoomRateLimitMax: number;
	createRoomRateLimitDurationMs: number;
	messageMaxContentLength: number;
};

function getFormFromConfig(data: RateLimitConfigResponse): Record<RateLimitFormKey, string> {
	return {
		windowAllowance: String(data.rlWindowAllowance),
		windowDurationS: String(data.rlWindowDurationMs / 1000),
		globalHttpRateLimitMax: String(data.globalHttpRateLimitMax),
		globalHttpRateLimitDurationS: String(data.globalHttpRateLimitDurationMs / 1000),
		adminLoginRateLimitMax: String(data.adminLoginRateLimitMax),
		adminLoginRateLimitDurationS: String(data.adminLoginRateLimitDurationMs / 1000),
		createRoomRateLimitMax: String(data.createRoomRateLimitMax),
		createRoomRateLimitDurationS: String(data.createRoomRateLimitDurationMs / 1000),
		messageMaxContentLength: String(data.messageMaxContentLength),
	};
}

export function RateLimiterSection() {
	const [form, setForm] = useState<Record<RateLimitFormKey, string>>(DEFAULT_RATE_LIMIT_FORM);
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api.admin.config.get().then(({ data }) => {
			if (!data) return;
			setForm(getFormFromConfig(data));
		});
	}, []);

	function updateField(key: RateLimitFormKey, value: string) {
		setForm((prev) => ({ ...prev, [key]: value }));
	}

	function getPositiveNumber(key: RateLimitFormKey) {
		const value = Number(form[key]);
		return Number.isInteger(value) && value > 0 ? value : null;
	}

	async function handleSave() {
		const values = Object.fromEntries(
			(Object.keys(form) as RateLimitFormKey[]).map((key) => [key, getPositiveNumber(key)]),
		) as Record<RateLimitFormKey, number | null>;

		if (Object.values(values).some((value) => value === null)) {
			setError("all fields must be positive whole numbers");
			return;
		}

		const { error } = await api.admin["update-rl-config"].post({
			windowAllowance: values.windowAllowance!,
			windowDurationMs: values.windowDurationS! * 1000,
			globalHttpRateLimitMax: values.globalHttpRateLimitMax!,
			globalHttpRateLimitDurationMs: values.globalHttpRateLimitDurationS! * 1000,
			adminLoginRateLimitMax: values.adminLoginRateLimitMax!,
			adminLoginRateLimitDurationMs: values.adminLoginRateLimitDurationS! * 1000,
			createRoomRateLimitMax: values.createRoomRateLimitMax!,
			createRoomRateLimitDurationMs: values.createRoomRateLimitDurationS! * 1000,
			messageMaxContentLength: values.messageMaxContentLength!,
		});

		if (error) {
			setError("failed to update config");
			return;
		}

		setError(null);
		setSaved(true);
		setTimeout(() => setSaved(false), 2000);
	}

	return (
		<div className="max-w-2xl space-y-6">
			<p className="text-xs text-[#333] font-mono">
				controls request and message limits. apply persists and updates this server immediately.
			</p>

			<div className="grid gap-4 sm:grid-cols-2">
				{RATE_LIMIT_FIELDS.map((field) => (
					<div key={field.key} className="space-y-2">
						<Label>{field.label}</Label>
						<Input
							type="number"
							min={1}
							value={form[field.key]}
							onChange={(e) => updateField(field.key, e.target.value)}
							placeholder={field.placeholder}
						/>
					</div>
				))}
			</div>

			{error && <p className="text-xs text-red-500 font-mono">// {error}</p>}

			<div className="flex gap-3">
				<Button size="sm" onClick={handleSave}>
					{saved ? "saved" : "apply"}
				</Button>
			</div>
		</div>
	);
}
